import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from "axios";
import { InteractionRequiredAuthError } from "@azure/msal-browser";
import { TOKEN_KEY } from "../../shared/utils/SessionConstants";
import { ENV } from "../../config";
import { getAuthToken } from "./SessionStorageService";
import { msalInstance, API_SCOPE } from "./msalInstance";

const BASE_URL = ENV.API_URL;

interface ExtendedAxiosRequestConfig extends AxiosRequestConfig {
  extractData?: boolean;
  skipAuthHeaders?: boolean;
  _retry?: boolean;
  overrideTenant?: string;
}

class ApiService {
  private readonly api: AxiosInstance;

  constructor() {
    this.api = axios.create({
      baseURL: BASE_URL,
    });

    // Request Interceptor
    this.api.interceptors.request.use(
      async (config) => {
        const skipAuth = (config as ExtendedAxiosRequestConfig).skipAuthHeaders;

        if (!skipAuth) {
          const overrideTenant = (config as ExtendedAxiosRequestConfig).overrideTenant;
          config.headers.tenant = overrideTenant ?? `${ENV.TENANT_ID}`;

          const accounts = msalInstance.getAllAccounts();
          if (accounts.length > 0) {
            // User is logged in via Azure AD — acquire the API token
            try {
              const response = await msalInstance.acquireTokenSilent({
                scopes: [API_SCOPE],
                account: accounts[0],
              });
              config.headers.Authorization = `Bearer ${response.accessToken}`;
            } catch (error) {
              if (error instanceof InteractionRequiredAuthError) {
                // Silent acquisition failed — fall back to popup
                try {
                  const response = await msalInstance.acquireTokenPopup({
                    scopes: [API_SCOPE],
                  });
                  config.headers.Authorization = `Bearer ${response.accessToken}`;
                } catch (popupError) {
                  console.error("[MSAL] Token acquisition failed:", popupError);
                  // Fall back to localStorage token so the request isn't sent unauthenticated
                  const AUTH_TOKEN = getAuthToken();
                  if (AUTH_TOKEN) config.headers.Authorization = `Bearer ${AUTH_TOKEN}`;
                }
              } else {
                console.error("[MSAL] Silent token acquisition failed:", error);
                // Fall back to localStorage token so the request isn't sent unauthenticated
                const AUTH_TOKEN = getAuthToken();
                if (AUTH_TOKEN) config.headers.Authorization = `Bearer ${AUTH_TOKEN}`;
              }
            }
          } else {
            // No Azure AD account — use the existing JWT token from storage
            const AUTH_TOKEN = getAuthToken();
            config.headers.Authorization = `Bearer ${AUTH_TOKEN}`;
          }
        }

        if (config.data instanceof FormData) {
          delete config.headers["Content-Type"];
        } else if (config.data !== undefined) {
          config.headers["Content-Type"] = "application/json";
        }

        return config;
      },
      (error) => Promise.reject(error),
    );

    // Response Interceptor
    this.api.interceptors.response.use(
      (response: AxiosResponse) => response,
      async (error) => {
        const status = error?.response?.status;
        const originalRequest = error?.config ?? error?.response?.config;
        const message =
          error?.response?.data || error?.message || "An error occurred";

        if (status === 401 && originalRequest && !originalRequest._retry) {
          // Don't attempt refresh for the login endpoint itself — would cause a loop
          if (originalRequest.url?.includes("auth/login")) {
            return Promise.reject(error);
          }

          // SSO path: if MSAL has an account, get a fresh token silently and retry
          const accounts = msalInstance.getAllAccounts();
          if (accounts.length > 0) {
            originalRequest._retry = true;
            try {
              const tokenResponse = await msalInstance.acquireTokenSilent({
                scopes: [API_SCOPE],
                account: accounts[0],
              });
              originalRequest.headers.Authorization = `Bearer ${tokenResponse.accessToken}`;
              return this.api.request(originalRequest);
            } catch (msalError) {
              console.error("[MSAL] Silent refresh failed on 401 retry:", msalError);
              return Promise.reject(error);
            }
          }

          // Don't attempt refresh if the refresh URL is not configured
          if (!ENV.API_URL || !ENV.APPLICATION_AUTHENTICATION_REFRESH_URL) {
            return Promise.reject(error);
          }

          originalRequest._retry = true;

          try {
            const { refreshToken } = await Promise.resolve().then(() =>
              require("../../features/AuthenticateView/services/LoginService"),
            );
            const refreshedAccessToken = await refreshToken(
              originalRequest.headers,
            );
            const accessToken =
              refreshedAccessToken ?? localStorage.getItem(TOKEN_KEY);

            if (accessToken) {
              if (!originalRequest.headers) {
                originalRequest.headers = {};
              }
              originalRequest.headers.Authorization = `Bearer ${accessToken}`;
            }

            return this.api.request(originalRequest);
          } catch (refreshError: any) {
            // Only redirect to /login when the refresh endpoint itself confirmed 401
            if (refreshError?.response?.status === 401) {
              localStorage.removeItem(TOKEN_KEY);
              window.location.replace("/login");
            }
            return Promise.reject(refreshError);
          }
        }

        if (
          (status === 401 && !originalRequest) ||
          String(message).includes("UnAuthorizedCustomException")
        ) {
          localStorage.removeItem(TOKEN_KEY);
          window.location.replace("/login");
        }

        return Promise.reject(message);
      },
    );
  }

  // Returns full AxiosResponse — use when headers/status are needed
  protected async makeRequest<T>(
    endpoint: string,
    options?: AxiosRequestConfig,
  ): Promise<AxiosResponse<T>>;

  // Returns response.data directly — use for standard JSON endpoints
  protected async makeRequest<T>(
    endpoint: string,
    options: ExtendedAxiosRequestConfig & { extractData: true },
  ): Promise<T>;

  protected async makeRequest<T>(
    endpoint: string,
    options?: ExtendedAxiosRequestConfig,
  ): Promise<AxiosResponse<T> | T> {
    try {
      const response = await this.api.request<T>({
        url: endpoint,
        method: options?.method ?? "GET",
        data: options?.data,
        headers: options?.headers,
        params: options?.params,
        responseType: options?.responseType ?? "json",
        skipAuthHeaders: options?.skipAuthHeaders,
        overrideTenant: options?.overrideTenant,
      } as ExtendedAxiosRequestConfig);

      return options?.extractData ? response.data : response;
    } catch (e: any) {
      return Promise.reject(e ?? "An error occurred");
    }
  }
}

export default ApiService;
